import { RefreshCw, Sparkles } from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import { Groups, StudyEditor } from "@/components/studies";
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Badge,
  BadgeText,
  Box,
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
  Center,
  Heading,
  HStack,
  StepIndicator,
  Text,
  VStack,
} from "@/components/ui";
import { StudyProvider, useLocalization } from "@/hooks";
import { Study } from "@/types/resources";

interface StudyDesignPreviewProps {
  /** A regenerate / cancel / create request is in flight. */
  busy: boolean;
  error: string | null;
  isOpen: boolean;
  onCancel: () => void;
  onCreate: () => void;
  onRegenerate: () => void;
  /** The new design is being generated and imported; the editors stay
      visible but inert until `study` changes. */
  regenerating: boolean;
  study: Study;
}

interface DraftStepsProps {
  disabled: boolean;
  study: Study;
}

/**
 * The study page's Audience / Questions steps, backed by the same `Groups`
 * and `StudyEditor` components so the draft is edited exactly like a study.
 */
const DraftSteps: FC<DraftStepsProps> = ({ disabled, study }) => {
  const { i18n } = useLocalization();
  const [step, setStep] = useState(0);
  const [totalRespondents, setTotalRespondents] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);

  const steps = useMemo(
    () => [
      i18n.t("audienceBuilder.stepAudience"),
      i18n.t("dashboardNavigator.questions"),
    ],
    [i18n]
  );

  const completedSteps = useMemo(() => {
    const completed: number[] = [];
    if (totalRespondents > 0) completed.push(0);
    if (totalQuestions > 0) completed.push(1);
    return completed;
  }, [totalQuestions, totalRespondents]);

  return (
    <StudyProvider studyId={study.id} studyType={study.type}>
      <VStack className="flex-1 min-h-0 gap-6">
        <StepIndicator
          completedSteps={completedSteps}
          current={step}
          onStepPress={setStep}
          steps={steps}
        />
        {/* Both steps stay mounted (like the study page) so switching tabs
            doesn't refetch or lose in-progress edits. */}
        <Box
          className="flex-1 min-h-0"
          style={step !== 0 ? { display: "none" } : undefined}
        >
          <Groups
            disabled={disabled}
            onContinue={() => setStep(1)}
            onTotalSizeChange={setTotalRespondents}
            resourceId={study.id}
            resourceType="study"
          />
        </Box>
        <Box
          className="flex-1 min-h-0"
          style={step !== 1 ? { display: "none" } : undefined}
        >
          <StudyEditor
            disabled={disabled}
            onNumQuestionsChange={setTotalQuestions}
            studyId={study.id}
            useCase={study.use_case ?? null}
          />
        </Box>
      </VStack>
    </StudyProvider>
  );
};

/**
 * Review dialog for an AI-designed study. The design is already imported as a
 * draft, so the body is the study page's own Audience / Questions editors;
 * Cancel and Regenerate delete the draft, Create keeps it and navigates to it.
 */
const StudyDesignPreview: FC<StudyDesignPreviewProps> = ({
  busy,
  error,
  isOpen,
  onCancel,
  onCreate,
  onRegenerate,
  regenerating,
  study,
}) => {
  const { i18n } = useLocalization();

  return (
    <AlertDialog
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={busy ? undefined : onCancel}
      size="lg"
    >
      <AlertDialogBackdrop />
      <AlertDialogContent className="gap-6 max-w-6xl h-[92vh]">
        <AlertDialogHeader>
          <HStack className="items-start gap-3 w-full">
            <Center className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-300/20 to-secondary-300/20">
              <Sparkles color="rgb(var(--color-primary-500))" size={24} />
            </Center>
            <VStack className="flex-1 gap-1">
              <HStack className="items-center gap-2">
                <Heading numberOfLines={1} size="lg">
                  {study.name}
                </Heading>
                <Badge className="rounded-full" size="sm" variant="outline">
                  <BadgeText>
                    {study.type === "quantitative"
                      ? i18n.t("studies.quantitativeShort")
                      : i18n.t("studies.qualitativeShort")}
                  </BadgeText>
                </Badge>
              </HStack>
              {study.description && (
                <Text className="text-sm text-typography-600">
                  {study.description}
                </Text>
              )}
            </VStack>
            <Button
              className="rounded-lg"
              isDisabled={busy}
              onPress={onRegenerate}
              variant="outline"
            >
              {regenerating ? (
                <ButtonSpinner color="rgb(var(--color-primary-500))" />
              ) : (
                <ButtonIcon as={RefreshCw} />
              )}
              <ButtonText>{i18n.t("studies.design.regenerate")}</ButtonText>
            </Button>
          </HStack>
        </AlertDialogHeader>

        {/* The body is a ScrollView; the editors scroll themselves, so the
            content container must fill it to give them a bounded height. */}
        <AlertDialogBody
          className="flex-1 min-h-0"
          contentContainerStyle={{ flex: 1 }}
        >
          <DraftSteps disabled={regenerating} key={study.id} study={study} />
        </AlertDialogBody>

        <AlertDialogFooter>
          <VStack className="gap-2 w-full">
            {error && (
              <Text className="text-sm text-error-600 text-right">{error}</Text>
            )}
            <HStack className="gap-3 justify-end w-full">
              <Button
                className="px-6 py-3 rounded-lg"
                isDisabled={busy}
                onPress={onCancel}
                variant="outline"
              >
                <ButtonText>{i18n.t("misc.cancel")}</ButtonText>
              </Button>
              <Button
                className="px-6 py-3 rounded-lg bg-primary-500"
                isDisabled={busy}
                onPress={onCreate}
              >
                <ButtonText className="text-white">
                  {i18n.t("studies.design.createStudy")}
                </ButtonText>
              </Button>
            </HStack>
          </VStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default StudyDesignPreview;
